import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Award, ArrowRight, MapPin, Clock, ShieldCheck } from "lucide-react";

const VendorCard = ({ vendor }) => {
  const navigate = useNavigate();

  // Helper to handle navigation and prevent "undefined" errors
  const handleNavigation = (e) => {
    e.stopPropagation();
    // MongoDB uses _id. We check both just in case.
    const targetId = vendor._id || vendor.id;

    if (targetId && targetId !== "undefined") {
      navigate(`/vendor-details/${targetId}`);
    } else {
      console.error("❌ Cannot navigate: Vendor Object is missing a valid ID", vendor);
    }
  };

  const avgRating =
    vendor.averageRating ??
    vendor.rating ??
    (vendor.reviews?.length
      ? vendor.reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / vendor.reviews.length
      : 0);

  return (
    <motion.div
      whileHover={{ y: -8 }}
      onClick={handleNavigation}
      className="relative bg-white rounded-3xl overflow-hidden cursor-pointer border border-slate-100 hover:border-cyan-100 shadow-md hover:shadow-2xl transition-all duration-500 flex flex-col h-[420px] group my-4"
    >
      {/* Header Background */}
      <div className={`h-24 transition-colors duration-500 ${vendor.isRecommended ? 'bg-gradient-to-br from-amber-50 to-orange-50 group-hover:from-amber-100' : 'bg-slate-50 group-hover:bg-cyan-50'}`} />

      {/* Recommended Badge */}
      {vendor.isRecommended && (
        <div className="absolute top-4 right-4 z-10">
          <div className="flex items-center gap-1.5 px-3 py-1 bg-white/95 backdrop-blur-md border border-amber-100 text-amber-600 text-[10px] font-black uppercase tracking-widest rounded-full shadow-sm">
            <Award className="w-3 h-3 fill-amber-500" />
            Top Expert
          </div>
        </div>
      )}

      {/* Profile Section */}
      <div className="px-6 -mt-12 flex flex-col items-center">
        <div className="relative">
          <div className="w-24 h-24 rounded-3xl overflow-hidden border-4 border-white shadow-lg bg-slate-100">
            <img
              src={vendor.profileImage || `https://ui-avatars.com/api/?name=${vendor.firstName}+${vendor.lastName}&background=random`}
              alt={vendor.firstName}
              className="w-full h-full object-cover transition-transform duration-500"
            />
          </div>
          {vendor.isActive && (
            <div className="absolute bottom-1 right-1 w-5 h-5 bg-green-500 border-4 border-white rounded-full shadow-sm"></div>
          )}
        </div>

        <div className="mt-6 w-full text-center">
          <h3 className="text-xl font-bold text-slate-800 group-hover:text-[#134DA7] transition-colors flex items-center justify-center gap-1">
            {vendor.firstName} {vendor.lastName}
            {vendor.documentsOverallStatus === "Verified" && (
              <ShieldCheck className="w-4 h-4 text-blue-500" fill="currentColor" />
            )}
          </h3>
          
          <div className="flex items-center justify-center gap-2 mt-1 mb-4">
            <span className="text-[10px] font-bold text-cyan-600 bg-cyan-50 px-3 py-1 rounded-md uppercase tracking-widest">
              {vendor.vendorType || "Professional"} 
            </span>
          </div>

          {/* Experience & Rating Bar */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-2xl mb-4">
            <div className="flex flex-col items-center flex-1">
              <span className="text-[9px] uppercase text-slate-400 font-bold">Experience</span>
              <div className="flex items-center gap-1 mt-0.5">
                <Clock className="w-3 h-3 text-slate-400" />
                <span className="text-sm font-bold text-slate-700">{vendor.experienceYears || 0} Yrs</span>
              </div>
            </div>
            <div className="w-px h-8 bg-slate-200"></div>
            <div className="flex flex-col items-center flex-1">
              <span className="text-[9px] uppercase text-slate-400 font-bold">Rating</span>
              <div className="flex items-center gap-1 mt-0.5">
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
             <span className="text-sm font-bold text-slate-700">
  {avgRating > 0 ? Number(avgRating).toFixed(1) : "4.9"}
</span>
              </div>
            </div>
          </div>

          {/* City Data */}
          <div className="flex items-center justify-center gap-2 text-slate-500 mb-6">
            <MapPin size={14} className="text-[#0097b2]" />
            <span className="text-xs font-medium truncate">
                {vendor.city || 'Available Online'}
            </span>
          </div>
        </div>
      </div>

      {/* CTA Button */}
      <div className="mt-auto p-6 pt-0">
        <button 
          onClick={handleNavigation}
          className="w-full flex items-center justify-center gap-2 py-4 bg-[#134DA7] group-hover:bg-[#0097b2] text-white text-sm font-bold rounded-2xl transition-all duration-300 shadow-lg shadow-blue-100"
        >
          <span>Consult Expert</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </motion.div>
  );
};

export default VendorCard;